import React, { useState } from 'react';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { WizardPanel } from '../components/WizardFrame';
import { STEP_NUMBER, WizardStep } from '../wizardModel';
import { PrimaryButton } from '../wizardStyles';
import { ErrorList } from './layout';

interface IStepPanelProps {
  /** The campaign wizard's step; another flow names its own and passes `stepNumber`. */
  step: WizardStep | string;
  /** "Step n/total"; defaults to the campaign wizard's own number for `step`. */
  stepNumber?: number | null;
  total?: number;
  narrow?: boolean;
  title: string;
  question: string;
  /** What still blocks Next; listed under the form once Next has been pressed. */
  errors: string[];
  onNext: () => void;
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * A numbered panel step (6778:18418): Next, or Finalise on Step 5/5, checks the step first. The
 * frames draw no error state, so the reasons are listed under the form, in the danger ink.
 */
const StepPanel: React.FC<IStepPanelProps> = ({
  step,
  title,
  question,
  errors,
  onNext,
  onBack,
  children,
  stepNumber,
  total = 5,
  narrow = false
}) => {
  const { t } = useMultiLanguageTranslation();
  const [attempted, setAttempted] = useState(false);
  const number = stepNumber !== undefined ? stepNumber : STEP_NUMBER[step as WizardStep] ?? null;
  const shown = attempted ? errors : [];

  return (
    <WizardPanel
      title={title}
      step={number}
      onBack={onBack}
      question={question}
      testId={`orbit-wizard-step-${step}`}
      total={total}
      narrow={narrow}
      footer={
        <PrimaryButton
          type="button"
          onClick={() => {
            setAttempted(true);
            if (errors.length === 0) {
              onNext();
            }
          }}
        >
          {t(`${TOrbit}.wizard.${number === total ? 'finalise' : 'next'}`)}
        </PrimaryButton>
      }
    >
      {children}
      {shown.length > 0 ? (
        <ErrorList role="alert">
          {shown.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ErrorList>
      ) : null}
    </WizardPanel>
  );
};

export default StepPanel;
